import React, { useMemo, useState } from 'react';
import type { CorrelationWidgetInstanceConfig, CorrelationData, CorrelationSeriesType } from './types';
import { CORRELATION_SERIES_OPTIONS, CORRELATION_HORIZON_OPTIONS } from './types';
import { formatFloat, buildTimeAxisFormatter, axisPrice } from '../../../design';
import type { AxisLabelFormatterParams } from '../../../design';
import { useDataProviderWidget } from '../../../blocks/hooks';
import { ChartWidget, ChartRangeSelector, redundantRangeValues } from '../../../blocks/chart-widget';
import type { ChartSpec } from '../../../blocks/chart-widget';
import type { LineChartSeries, ChartLegendSeries } from '../../../blocks/charts';
import { SEMANTIC_CHART_COLORS } from '../../../design';

// Sample data shown during dashboard construction (no asset assigned)
const SAMPLE_DATA: CorrelationData = {
  ticker: 'AAPL',
  compare_ticker: 'SPY',
  series_a_label: 'AAPL Price',
  series_b_label: 'SPY Price',
  series_a: [
    { date: '2025-01-02', value: 172.10 },
    { date: '2025-01-15', value: 175.30 },
    { date: '2025-01-31', value: 178.90 },
    { date: '2025-02-14', value: 176.20 },
    { date: '2025-02-28', value: 181.50 },
    { date: '2025-03-07', value: 184.80 },
    { date: '2025-03-14', value: 183.10 },
    { date: '2025-03-21', value: 187.44 },
  ],
  series_b: [
    { date: '2025-01-02', value: 468.20 },
    { date: '2025-01-15', value: 472.50 },
    { date: '2025-01-31', value: 478.10 },
    { date: '2025-02-14', value: 475.80 },
    { date: '2025-02-28', value: 481.30 },
    { date: '2025-03-07', value: 485.70 },
    { date: '2025-03-14', value: 483.90 },
    { date: '2025-03-21', value: 489.20 },
  ],
  correlation_series: [
    { date: '2025-01-31', correlation: 0.82 },
    { date: '2025-02-14', correlation: 0.78 },
    { date: '2025-02-28', correlation: 0.85 },
    { date: '2025-03-07', correlation: 0.91 },
    { date: '2025-03-14', correlation: 0.88 },
    { date: '2025-03-21', correlation: 0.86 },
  ],
  overall_correlation: 0.85,
};

interface CorrelationWidgetRendererProps {
  config: CorrelationWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: unknown) => void;
  readOnly?: boolean;
  widgetInstanceId?: string;
}

function correlationColor(v: number | null): string {
  if (v === null) return 'text-neutral-500';
  const abs = Math.abs(v);
  if (abs >= 0.7) return v > 0 ? 'text-emerald-600' : 'text-red-600';
  if (abs >= 0.4) return v > 0 ? 'text-emerald-500' : 'text-red-500';
  return 'text-neutral-500';
}

function correlationLabel(v: number | null): string {
  if (v === null) return '—';
  const abs = Math.abs(v);
  if (abs >= 0.7) return abs >= 0.9 ? 'Very Strong' : 'Strong';
  if (abs >= 0.4) return 'Moderate';
  if (abs >= 0.2) return 'Weak';
  return 'Very Weak';
}

const axisCorrFormatter = (params: AxisLabelFormatterParams) => {
  if (params.value == null) return '';
  return Number(params.value).toFixed(2);
};

/** Minimal inline select — blends into the toolbar */
const inlineSelect =
  'h-[22px] pl-1.5 pr-4 py-0 rounded text-[11px] font-medium tracking-wide uppercase ' +
  'bg-neutral-100 text-neutral-600 border-0 cursor-pointer ' +
  'hover:bg-neutral-200 transition-colors ' +
  'appearance-none bg-[length:8px] bg-[right_3px_center] bg-no-repeat ' +
  'bg-[url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 12 12%27%3E%3Cpath d=%27M3 5l3 3 3-3%27 fill=%27none%27 stroke=%27%23888%27 stroke-width=%271.5%27/%3E%3C/svg%3E")]';

/** Inline text input — matches select styling */
const inlineInput =
  'h-[22px] w-16 px-1.5 rounded text-[11px] font-semibold tracking-wide uppercase ' +
  'bg-neutral-100 text-neutral-700 border-0 ' +
  'placeholder:text-neutral-400 placeholder:font-normal placeholder:normal-case ' +
  'hover:bg-neutral-200 focus:bg-neutral-200 focus:outline-none transition-colors';

/** Vertical separator between header groups */
const Sep: React.FC = () => (
  <div className="w-px h-3.5 bg-neutral-200 mx-0.5" />
);

const CorrelationWidgetRenderer: React.FC<CorrelationWidgetRendererProps> = ({
  config,
  dashboardData = {},
  widgetInstanceId,
  readOnly = false,
}) => {
  const uid = dashboardData?.assetDataUid as string | undefined;
  const isPreview = !uid;

  // Live config state — seeded from widget config, user can change via dropdowns
  const [seriesA, setSeriesA] = useState<CorrelationSeriesType>(config.series_a || 'price');
  const [seriesB, setSeriesB] = useState<CorrelationSeriesType>(config.series_b || 'price');
  const [entityB, setEntityB] = useState(config.entity_b || 'SPY');
  const [horizon, setHorizon] = useState(config.horizon ?? 504);

  // Merge live state into the config sent to the data-provider.
  // horizon passes through as-is — including 0 ("All"). Omitting it would make
  // useDataProviderWidget re-inject the definition's default (504), so "All"
  // would silently behave as 2Y and never even trigger a refetch; the backend
  // skips trimming when horizon is 0.
  const liveConfig = useMemo(() => ({
    ...config,
    series_a: seriesA,
    series_b: seriesB,
    entity_b: entityB || undefined,
    horizon,
  }), [config, seriesA, seriesB, entityB, horizon]);

  const { data, loading, error, refetch } = useDataProviderWidget<CorrelationData>(
    'correlation',
    uid,
    liveConfig as unknown as Record<string, unknown>,
    { enabled: !!uid, widgetInstanceId },
  );

  const displayData = uid ? data : SAMPLE_DATA;

  // Merge all three series into a single dataset keyed by date
  const chartData = useMemo(() => {
    if (!displayData?.series_a) return [];

    const bMap = new Map<string, number>();
    if (displayData.series_b) {
      for (const row of displayData.series_b) bMap.set(row.date, row.value);
    }
    const corrMap = new Map<string, number>();
    if (displayData.correlation_series) {
      for (const row of displayData.correlation_series) corrMap.set(row.date, row.correlation);
    }

    const points = displayData.series_a.map((row) => {
      const point: Record<string, unknown> = {
        date: row.date,
        valueA: row.value,
      };
      const bVal = bMap.get(row.date);
      if (bVal !== undefined) point.valueB = bVal;
      const corr = corrMap.get(row.date);
      if (corr !== undefined) point.correlation = corr;
      return point;
    });
    // Series arrive newest-first; the chart reads left→right (oldest→newest) and
    // the legend/end-tags treat the last row as "latest" — so sort ascending by
    // date, mirroring the PRICE/other chart widgets.
    points.sort((a, b) => (a.date as string).localeCompare(b.date as string));
    return points;
  }, [displayData]);

  // Only disable redundant pills when horizon=0 (All) — only then do we have
  // the complete dataset and can reliably tell which sized pills are no-ops.
  // For any sized horizon we can't know if larger pills would return more data.
  const disabledRanges = useMemo(() => {
    if (horizon !== 0) return new Set<string>();
    return redundantRangeValues(
      CORRELATION_HORIZON_OPTIONS
        .filter(o => o.rows != null)
        .map(o => ({ value: String(o.value), label: o.label, rows: o.rows })),
      chartData,
    );
  }, [horizon, chartData]);

  const labelA = displayData?.series_a_label || displayData?.ticker || 'A';
  const labelB = displayData?.series_b_label || displayData?.compare_ticker || 'B';
  const hasBSeries = !!(displayData?.series_b && displayData.series_b.length > 0);

  // 2-axis series: correlation on the left, both price series share one right axis
  // (a single price scale reads cleaner than two overlapping right axes, and the
  // chart is about co-movement, not absolute price comparison).
  // Semantic palette: correlation is a derived measure (amber/caution); the two
  // priced entities are subject (blue) and benchmark (green).
  const series = useMemo<LineChartSeries[]>(() => {
    const result: LineChartSeries[] = [
      { yKey: 'correlation', yName: 'Correlation', markers: false, axis: 'corr', color: SEMANTIC_CHART_COLORS.caution },
      { yKey: 'valueA', yName: labelA, markers: false, axis: 'price', color: SEMANTIC_CHART_COLORS.subject },
    ];
    if (hasBSeries) {
      result.push({ yKey: 'valueB', yName: labelB, markers: false, axis: 'price', color: SEMANTIC_CHART_COLORS.benchmark });
    }
    return result;
  }, [labelA, labelB, hasBSeries]);

  const dates = useMemo(() => chartData.map((d) => d.date as string), [chartData]);
  const timeFormatter = useMemo(() => buildTimeAxisFormatter(dates), [dates]);

  const yLabelFormatters = useMemo(() => ({
    corr: axisCorrFormatter,
    price: axisPrice,
  }), []);

  const legendSeries = useMemo<ChartLegendSeries[]>(() => {
    const lastRow = chartData.length > 0 ? chartData[chartData.length - 1] : null;
    return series.map((s) => ({
      key: s.yKey,
      name: s.yName,
      color: s.color || SEMANTIC_CHART_COLORS.subject,
      value: lastRow && typeof lastRow[s.yKey] === 'number' ? lastRow[s.yKey] as number : null,
      formatter: (v: number | string) => formatFloat(Number(v), 2),
    }));
  }, [series, chartData]);

  const overallCorr = displayData?.overall_correlation ?? null;
  const entityA = displayData?.ticker || '—';

  const spec: ChartSpec = {
    chartKind: 'line',
    rows: chartData,
    xKey: 'date',
    lineSeries: series,
    showEndValueTags: true,
    xLabelFormatter: timeFormatter,
    xTitle: 'Date',
    yLabelFormatters,
    yTitles: { corr: 'Correlation', price: 'Price ($)' },
    yAxisColors: { corr: SEMANTIC_CHART_COLORS.caution },
    legendSeries,
    footnote: 'Source: FMP',
    emptyMessage: 'No data available',
  };

  const toolbar = (
      <div className="flex items-center gap-1.5 px-2.5 py-1 border-b border-neutral-100 shrink-0">
        {/* Variable A */}
        <div className="flex items-center gap-1">
          <span className="text-[11px] font-semibold text-neutral-800 tracking-wide">{entityA}</span>
          {!readOnly ? (
            <select
              value={seriesA}
              onChange={(e) => setSeriesA(e.target.value as CorrelationSeriesType)}
              className={inlineSelect}
            >
              {CORRELATION_SERIES_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          ) : (
            <span className="text-[11px] font-medium text-neutral-500 uppercase tracking-wide">
              {CORRELATION_SERIES_OPTIONS.find((o) => o.value === seriesA)?.label || seriesA}
            </span>
          )}
        </div>

        <span className="text-[10px] text-neutral-400">vs</span>

        {/* Variable B */}
        <div className="flex items-center gap-1">
          {!readOnly ? (
            <input
              type="text"
              value={entityB}
              onChange={(e) => setEntityB(e.target.value.toUpperCase())}
              placeholder={entityA}
              className={inlineInput}
            />
          ) : (
            <span className="text-[11px] font-semibold text-neutral-800 tracking-wide">
              {entityB || entityA}
            </span>
          )}
          {!readOnly ? (
            <select
              value={seriesB}
              onChange={(e) => setSeriesB(e.target.value as CorrelationSeriesType)}
              className={inlineSelect}
            >
              {CORRELATION_SERIES_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          ) : (
            <span className="text-[11px] font-medium text-neutral-500 uppercase tracking-wide">
              {CORRELATION_SERIES_OPTIONS.find((o) => o.value === seriesB)?.label || seriesB}
            </span>
          )}
        </div>

        <Sep />

        {/* Horizon — always interactive; pills recompute the correlation server-side.
            A saved custom horizon (free-form builder input) gets a transient pill so
            the control reflects the actual fetch instead of looking unset. */}
        <ChartRangeSelector
          compact
          options={[
            ...CORRELATION_HORIZON_OPTIONS.map((o) => ({ value: String(o.value), label: o.label, rows: o.rows })),
            ...(CORRELATION_HORIZON_OPTIONS.some((o) => o.value === horizon)
              ? []
              : [{ value: String(horizon), label: `${horizon}D` }]),
          ]}
          value={String(horizon)}
          onChange={(v) => setHorizon(Number(v))}
          disabledValues={disabledRanges}
        />

        {/* Correlation readout — right-aligned */}
        {overallCorr !== null && (
          <>
            <div className="ml-auto" />
            <span className="text-[10px] font-medium text-neutral-400 uppercase tracking-wider">Corr</span>
            <span className={`text-sm font-bold tabular-nums ${correlationColor(overallCorr)}`}>
              {formatFloat(overallCorr, 3)}
            </span>
            <span className={`text-[10px] font-medium tracking-wide ${correlationColor(overallCorr)}`}>
              {correlationLabel(overallCorr)}
            </span>
          </>
        )}
      </div>
  );

  return (
    <ChartWidget
      loading={loading}
      error={error}
      refetch={refetch}
      isPreview={isPreview}
      spec={spec}
      toolbar={toolbar}
    />
  );
};

export default CorrelationWidgetRenderer;
